import { BoundsShape } from '../models/QueryModel';
import { LngLatGeolocation } from './Geolocation';

export interface BalloonEventsQuery {
  id?: string;
  amount?: number;
  bbox?:
    | {
        northEast: LngLatGeolocation;
        southWest: LngLatGeolocation;
      }
    | BoundsShape;
  regionArea?: string[];
  region?: string[];
  text?: string;
  categories?: string[];
  dates?: Date[];
  tags?: string[];
  sources?: string[];
  /** Scope results to events created by these user slugs (user-profile map mode). */
  user?: string[];
  /** Scope results to these data-source-provider short names (partner-profile map mode). */
  providers?: string[];
  mustHaveAtLeastOneImage?: boolean;
  /**
   * Map zoom level used for /api/v3/search clustering decisions.
   * Ignored by /api/search (v2). Optional so v2 callers are unaffected.
   */
  zoom?: number;
  /**
   * Saved-search opt-in: when true, the frontend turns on the supercluster code path.
   * Default behavior (undefined / false) renders individual pins.
   */
  forceMapClustering?: boolean;
}

export function includesQuery(
  base: BalloonEventsQuery,
  query: BalloonEventsQuery
): boolean {
  if (
    Object.keys(query).length === 0 ||
    (Object.keys(query).length === 1 && query.id !== undefined)
  ) {
    return false;
  }
  if (query.amount !== undefined && base.amount !== query.amount) {
    return false;
  }
  if (query.bbox !== undefined && base.bbox !== query.bbox) {
    return false;
  }
  if (query.regionArea !== undefined) {
    if (base.regionArea === undefined) return false;
    for (const regionArea of query.regionArea) {
      if (!base.regionArea.includes(regionArea)) {
        return false;
      }
    }
  }
  if (query.region !== undefined) {
    if (base.region === undefined) return false;
    for (const region of query.region) {
      if (!base.region.includes(region)) {
        return false;
      }
    }
  }
  if (query.text !== undefined && base.text !== query.text) {
    return false;
  }
  if (query.categories !== undefined) {
    if (base.categories === undefined) return false;
    for (const category of query.categories) {
      if (!base.categories.includes(category)) {
        return false;
      }
    }
  }
  if (query.dates !== undefined) {
    if (base.dates === undefined) return false;
    for (const date of query.dates) {
      if (
        !base.dates.some((baseDate) => baseDate.getTime() === date.getTime())
      ) {
        return false;
      }
    }
  }
  if (query.tags !== undefined) {
    if (base.tags === undefined) return false;
    for (const tag of query.tags) {
      if (!base.tags.includes(tag)) {
        return false;
      }
    }
  }
  if (query.sources !== undefined) {
    if (base.sources === undefined) return false;
    for (const source of query.sources) {
      if (!base.sources.includes(source)) {
        return false;
      }
    }
  }
  if (query.user !== undefined) {
    if (base.user === undefined) return false;
    for (const user of query.user) {
      if (!base.user.includes(user)) {
        return false;
      }
    }
  }
  if (query.providers !== undefined) {
    if (base.providers === undefined) return false;
    for (const provider of query.providers) {
      if (!base.providers.includes(provider)) {
        return false;
      }
    }
  }
  if (query.zoom !== undefined && base.zoom !== query.zoom) {
    return false;
  }
  if (
    query.forceMapClustering !== undefined &&
    base.forceMapClustering !== query.forceMapClustering
  ) {
    return false;
  }
  return true;
}

export function mergeQueries(
  queries: BalloonEventsQuery[]
): BalloonEventsQuery {
  const newQuery: BalloonEventsQuery = {};

  for (const query of queries) {
    if (query.amount !== undefined) {
      newQuery.amount = query.amount;
    }
    if (query.bbox !== undefined) {
      newQuery.bbox = query.bbox;
    }
    if (query.dates !== undefined && query.dates.length > 0) {
      newQuery.dates = query.dates;
    }
    if (query.text !== undefined) {
      newQuery.text = query.text;
    }
    if (query.categories !== undefined && query.categories.length > 0) {
      if (newQuery.categories === undefined) {
        newQuery.categories = [...query.categories];
      } else {
        newQuery.categories.push(...query.categories);
      }
    }
    if (query.sources !== undefined && query.sources.length > 0) {
      if (newQuery.sources === undefined) {
        newQuery.sources = [...query.sources];
      } else {
        newQuery.sources.push(...query.sources);
      }
    }
    if (query.tags !== undefined && query.tags.length > 0) {
      if (newQuery.tags === undefined) {
        newQuery.tags = [...query.tags];
      } else {
        newQuery.tags.push(...query.tags);
      }
    }
    if (query.user !== undefined && query.user.length > 0) {
      if (newQuery.user === undefined) {
        newQuery.user = [...query.user];
      } else {
        newQuery.user.push(...query.user);
      }
    }
    if (query.providers !== undefined && query.providers.length > 0) {
      if (newQuery.providers === undefined) {
        newQuery.providers = [...query.providers];
      } else {
        newQuery.providers.push(...query.providers);
      }
    }
    if (query.regionArea !== undefined && query.regionArea.length > 0) {
      newQuery.regionArea = [...query.regionArea];
      newQuery.region = [];
    }
    if (query.region !== undefined && query.region.length > 0) {
      newQuery.region = [...query.region];
      newQuery.regionArea = [];
    }
    if (query.mustHaveAtLeastOneImage !== undefined) {
      newQuery.mustHaveAtLeastOneImage = query.mustHaveAtLeastOneImage;
    }
    if (query.zoom !== undefined) {
      newQuery.zoom = query.zoom;
    }
    if (query.forceMapClustering !== undefined) {
      newQuery.forceMapClustering = query.forceMapClustering;
    }
  }
  // Dedup string-array fields. Multiple query layers (e.g. /tags/<slug> + a
  // saved-search-by-tag layer) can both contribute the same slug; the backend
  // tolerates duplicates but they bloat the URL and confuse logs.
  if (newQuery.tags) newQuery.tags = Array.from(new Set(newQuery.tags));
  if (newQuery.user) newQuery.user = Array.from(new Set(newQuery.user));
  if (newQuery.providers) newQuery.providers = Array.from(new Set(newQuery.providers));
  if (newQuery.categories) newQuery.categories = Array.from(new Set(newQuery.categories));
  if (newQuery.sources) newQuery.sources = Array.from(new Set(newQuery.sources));
  if (newQuery.region) newQuery.region = Array.from(new Set(newQuery.region));
  if (newQuery.regionArea) newQuery.regionArea = Array.from(new Set(newQuery.regionArea));
  return newQuery;
}
